import React, { useEffect, useState } from "react";
import { Col, Label, Row } from "reactstrap";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";

import { APP_ROUTES } from "../../helpers/routeHelpers";
import { FORM_VALIDATION_SCHEMA } from "./FormValidation";
import { TextInput } from "../../components/Input";
import { LABELS, NAMES, PLACEHOLDERS } from "./Config";
import { FormikErrorText } from "../../components/FormikErrorText";
import RadioGroup from "./RadioGroup";
import { AutoComplete } from "../../components/AutoComplete";
import { useRequestProcessor } from "../../hooks/useRequestProcessors";
import { getKey, MODULE_KEYS } from "../../api/api.keys";
import { GetAgeBracket } from "../../api/api.service";
import { customStyles } from "../UserManagement/CustomStyles";
import DatePickerSingle from "./DatePickerSingle";
import TimePicker from "./TimePicker";
import { CustomButton } from "../../components/CustomButton";
import SubscriptionStatus from "../../constants/SubscriptionStatus";
import { NoticationType } from "../../constants";

const EDIT = "edit";

const mapToOptions = (data, labelKey, valueKey, valueSubKey) => {
    if (!Array.isArray(data)) return [];

    return data
        .filter((item) => item[labelKey] !== undefined) // Ensure `startAge` exists
        .map(({ [labelKey]: label, [valueKey]: value, [valueSubKey]: endAge }) => {
            if (endAge === 0 || endAge === undefined) {
                label = `${label}+`;
            } else {
                label = `${label} - ${endAge}`;
            }

            return {
                label,
                value
            };
        });
};

let {
    TITLE,
    NOTIFICATION_TYPE,
    NOTIFICATION_MESSAGE,
    SCHEDULE_DATE,
    SCHEDULE_TIME,
    AGE_GROUP,
    USER_GROUP
} = LABELS;

const NotificationForm = ({
    mode,
    isLoading,
    data,
    onUpdateNotification,
    onAddNotification,
    isDisabled
}) => {
    let isAudioEditable = mode === EDIT;
    const history = useHistory();
    const { useFetch } = useRequestProcessor();
    const [currentPage, setCurrentPage] = useState(1);
    const [hasAgeBracket, setHasAgeBracket] = useState(true);
    const [ageBracketOptions, setAgeBracketOptions] = useState([]);

    const updatedInitialValues = {
        [NAMES[TITLE]]: data?.title,
        [NAMES[AGE_GROUP]]: isAudioEditable
            ? data?.appNotificationAgeBrackets?.map((ageGroup) => {
                  const { startAge, endAge, id } = ageGroup?.ageBracket || {};
                  return {
                      value: id,
                      label: endAge === 0 ? `${startAge}+` : `${startAge} - ${endAge}`
                  };
              })
            : null,
        [NAMES[SCHEDULE_DATE]]: isAudioEditable ? new Date(data?.scheduledAt) : null,
        [NAMES[SCHEDULE_TIME]]: isAudioEditable ? new Date(data?.scheduledAt) : null,
        [NAMES[NOTIFICATION_MESSAGE]]: data?.content,

        [NAMES[NOTIFICATION_TYPE]]: NoticationType.getDisplayTextKey(data?.type),
        [NAMES[USER_GROUP]]: SubscriptionStatus.getDisplayTextKey(data?.tierType)
    };

    const formik = useFormik({
        initialValues: updatedInitialValues,
        validationSchema: FORM_VALIDATION_SCHEMA,
        onSubmit: (values) => {
            if (isAudioEditable) {
                onUpdateNotification(values);
            } else {
                onAddNotification(values);
            }
        }
    });

    const { data: ageBracketData, isFetching: isFetchingAgeBracket } = useFetch(
        getKey([MODULE_KEYS.AUDIOS_SUB_CATEGORY, currentPage], {}),
        async ({ signal }) =>
            await GetAgeBracket({
                params: { page: currentPage, limit: 10 },
                signal
            }),
        { keepPreviousData: true, enabled: hasAgeBracket }
    );

    useEffect(() => {
        if (ageBracketData?.data) {
            const newOptions = mapToOptions(ageBracketData?.data, "startAge", "id", "endAge");
            setAgeBracketOptions((prev) => [...prev, ...newOptions]);
            if (ageBracketOptions.length + newOptions?.length >= ageBracketData?.total) {
                setHasAgeBracket(false);
            }
        }
    }, [ageBracketData?.data]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 20) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    return (
        <form onSubmit={formik.handleSubmit} className="p-2">
            <Row>
                <Col md={6}>
                    <TextInput
                        placeholder={PLACEHOLDERS[TITLE]}
                        label={TITLE}
                        name={NAMES[TITLE]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[NAMES[TITLE]]}
                        className="hide-default-icon"
                        disabled={isDisabled}
                        autoFocus
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[TITLE]} />
                </Col>
                <Col md={6}>
                    <p className="input-label">{NOTIFICATION_TYPE}</p>
                    <RadioGroup
                        field={NAMES[NOTIFICATION_TYPE]}
                        value={formik.values[NAMES[NOTIFICATION_TYPE]]}
                        setFieldValue={formik.setFieldValue}
                        options={["Recommendation", "Promotional"]}
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[NOTIFICATION_TYPE]} />
                </Col>
            </Row>

            <Row className="my-4">
                <TextInput
                    placeholder={PLACEHOLDERS[NOTIFICATION_MESSAGE]}
                    label={NOTIFICATION_MESSAGE}
                    name={NAMES[NOTIFICATION_MESSAGE]}
                    type="textarea"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[NAMES[NOTIFICATION_MESSAGE]]}
                    className="hide-default-icon"
                    disabled={isDisabled}
                />
                <FormikErrorText formikInstance={formik} fieldName={NAMES[NOTIFICATION_MESSAGE]} />
            </Row>
            <Row>
                <Col md={6}>
                    <AutoComplete
                        classNamePrefix="status-header-search-admin"
                        isLoading={isFetchingAgeBracket}
                        handleScroll={(e) => handleScroll(e)}
                        label={AGE_GROUP}
                        value={formik.values[NAMES[AGE_GROUP]]}
                        onChange={(option) =>
                            formik.setFieldValue(NAMES[AGE_GROUP], option || null)
                        }
                        onBlur={() => formik.setFieldTouched(NAMES[AGE_GROUP])}
                        placeholder={PLACEHOLDERS[AGE_GROUP]}
                        options={ageBracketOptions}
                        maxMenuHeight={200}
                        customStyles={customStyles.filter}
                        isDisabled={isDisabled}
                        isMulti
                        isClearable
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[AGE_GROUP]} />
                </Col>

                <Col md={6}>
                    <p className="input-label">{USER_GROUP}</p>
                    <RadioGroup
                        field={NAMES[USER_GROUP]}
                        value={formik.values[NAMES[USER_GROUP]]}
                        setFieldValue={formik.setFieldValue}
                        options={["All", "Premium", "Freemium"]}
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[USER_GROUP]} />
                </Col>
            </Row>

            <Row className="my-4">
                <Col md={6}>
                    <p className="mb-2 input-label">{SCHEDULE_DATE}</p>
                    <DatePickerSingle formik={formik} fieldName={NAMES[SCHEDULE_DATE]} />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[SCHEDULE_DATE]} />
                </Col>

                <Col md={6}>
                    <p className="mb-2 input-label">{SCHEDULE_TIME}</p>
                    <TimePicker formik={formik} fieldName={NAMES[SCHEDULE_TIME]} />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[SCHEDULE_TIME]} />
                </Col>
            </Row>
            <Row className="my-2">
                <div className="d-flex justify-content-end w-100  mt-4">
                    <CustomButton
                        type="button"
                        title="Cancel"
                        className="me-3 custom-btn-sm"
                        color="primary"
                        outline
                        onClick={
                            mode === "add"
                                ? () => formik.resetForm()
                                : () => history.push(APP_ROUTES.NOTIFICATION_MANAGEMENT)
                        }
                        disabled={isLoading}
                    />
                    <CustomButton
                        loading={isLoading}
                        type="submit"
                        title={mode === "add" ? "Save" : "Update"}
                        color="primary"
                        className="shadow-theme grad-primary"
                    />
                </div>
            </Row>
        </form>
    );
};

export default NotificationForm;
